import React, { FormEvent, SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, AutocompleteInputChangeReason, Button, IconButton, InputAdornment, MenuItem, Paper, Stack, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { SendIcon } from 'src/icons/SendIcon';
import {
    useInviteParticipantListMutation,
    useInviteUserMutation,
    useSearchParticipantListsQuery,
} from 'src/api/v1/contact-ApiSlice';
import { ParticipantList } from 'src/model/api/contact.model';
import { useAppDispatch } from 'src/app/hooks';
import { showSnackBar } from 'src/api/ui-slice';
import { ProjectRole } from 'src/model/api/v2/data-contracts';
import { getV1ErrorMessage } from 'src/utils/v1ErrorMessage';
import { EMAIL_PATTERN } from 'src/utils/email';
import { ts } from 'src/utils/translate';
import { DEFAULT_SPACING } from 'src/theme/style-consts';

const SEARCH_DEBOUNCE_MS = 300;

type Props = {
    projectId: number;
    roles: ProjectRole[];
    disabled: boolean;
    onClose: () => void;
};

export const InviteUserForm = ({ projectId, roles, disabled, onClose }: Props) => {
    const dispatch = useAppDispatch();
    const [inviteUser, { isLoading: isInvitingUser }] = useInviteUserMutation();
    const [inviteParticipantList, { isLoading: isInvitingList }] = useInviteParticipantListMutation();
    const isLoading = isInvitingUser || isInvitingList;

    const [email, setEmail] = useState('');
    const [debouncedEmail, setDebouncedEmail] = useState('');
    const [roleId, setRoleId] = useState<number | ''>(roles[0]?.id ?? '');
    const [showEmailError, setShowEmailError] = useState(false);
    const [selectedList, setSelectedList] = useState<ParticipantList | null>(null);

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedEmail(email.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [email]);

    const { data: participantLists = [] } = useSearchParticipantListsQuery(debouncedEmail, {
        skip: debouncedEmail === '' || selectedList != null,
    });

    const isEmailValid = EMAIL_PATTERN.test(email.trim());

    const showInviteError = (error: unknown) => {
        dispatch(showSnackBar({
            text: getV1ErrorMessage(error, ts('project_users_invite_error', 'Failed to send the invitation')),
            severity: 'error',
        }));
    };

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault();
        if (!isEmailValid || roleId === '') {
            setShowEmailError(true);
            return;
        }
        inviteUser({
            email: email.trim(),
            role_id: roleId,
            project_id: projectId,
        })
            .unwrap()
            .then((response) => {
                dispatch(showSnackBar({ text: response.message, severity: 'success' }));
                onClose();
            })
            .catch(showInviteError);
    };

    const handleListInvite = () => {
        if (selectedList == null) {
            return;
        }
        inviteParticipantList({
            list_id: selectedList.id,
            project_id: projectId,
        })
            .unwrap()
            .then((response) => {
                if (response?.message) {
                    dispatch(showSnackBar({ text: response.message, severity: 'success' }));
                }
            })
            .catch(showInviteError)
            .finally(onClose);
    };

    const handleEmailInputChange = (_event: SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
        if (reason !== 'reset') {
            setEmail(value);
        }
    };

    const handleParticipantListSelect = (_event: SyntheticEvent, value: ParticipantList | string | null) => {
        if (value != null && typeof value !== 'string') {
            setSelectedList(value);
        }
    };

    const handleListCancel = () => {
        setSelectedList(null);
        setEmail('');
    };

    if (selectedList != null) {
        return (
            <Paper variant="outlined" sx={{ padding: DEFAULT_SPACING }}>
                <Stack spacing={DEFAULT_SPACING}>
                    <TextField
                        size="small"
                        value={selectedList.name}
                        slotProps={{
                            input: {
                                readOnly: true,
                                sx: { fontWeight: 600 },
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton size="small" onClick={handleListCancel} disabled={isLoading}>
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            },
                        }}
                        disabled={disabled}
                    />
                    <TextField select size="small" value="participant_list" disabled>
                        <MenuItem value="participant_list">
                            {ts('project_users_participant_list', 'Participant List')}
                        </MenuItem>
                    </TextField>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                            {ts('plt_cancel', 'Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleListInvite}
                            disabled={disabled || isLoading}
                        >
                            {ts('project_users_send_list_invitation', 'Send invitation')}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>
        );
    }

    return (
        <Paper variant="outlined" sx={{ padding: DEFAULT_SPACING }}>
            <Stack component="form" spacing={DEFAULT_SPACING} onSubmit={handleSubmit} noValidate>
                <Autocomplete<ParticipantList, false, false, true>
                    freeSolo
                    value={null}
                    inputValue={email}
                    onInputChange={handleEmailInputChange}
                    onChange={handleParticipantListSelect}
                    options={debouncedEmail === '' ? [] : participantLists}
                    filterOptions={(options) => options}
                    getOptionLabel={(option) => typeof option === 'string' ? option : option.name}
                    groupBy={() => ts('pl_title', 'Participant Lists')}
                    disabled={disabled || isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            autoFocus
                            size="small"
                            type="email"
                            placeholder={ts('project_users_invite_email_placeholder', 'Email address or Participant List name')}
                            error={showEmailError && !isEmailValid}
                            helperText={showEmailError && !isEmailValid
                                ? ts('pl_form_error', 'Please enter a valid e-mail address or select a Participant List.')
                                : undefined}
                        />
                    )}
                />
                <TextField
                    select
                    size="small"
                    value={roleId}
                    onChange={(event) => setRoleId(Number(event.target.value))}
                    disabled={disabled || isLoading}
                >
                    {roles.map((role) => (
                        <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
                    ))}
                </TextField>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="outlined" onClick={onClose} disabled={isLoading}>
                        {ts('plt_cancel', 'Cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<SendIcon />}
                        disabled={disabled || isLoading}
                    >
                        {ts('project_users_send_invitation', 'Send Invitation')}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};
